
import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Form, FormField, FormItem, FormLabel, FormControl, FormDescription } from "@/components/ui/form";
import { useForm } from "react-hook-form";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Palette, Layout, Image } from "lucide-react";

interface AppearanceSettings {
  theme: string;
  logoEnabled: boolean;
  customHeaderColor: string;
  customAccentColor: string;
}

interface ParentPortalAppearanceTabProps {
  initialSettings?: {
    theme?: string;
    logoEnabled?: boolean;
    customHeaderColor?: string;
    customAccentColor?: string;
  };
  onSettingsChange?: (settings: AppearanceSettings) => void;
  readOnly?: boolean;
}

const ParentPortalAppearanceTab: React.FC<ParentPortalAppearanceTabProps> = ({
  initialSettings,
  onSettingsChange,
  readOnly = false,
}) => {
  const form = useForm<AppearanceSettings>({
    defaultValues: {
      theme: initialSettings?.theme || 'default',
      logoEnabled: initialSettings?.logoEnabled !== false,
      customHeaderColor: initialSettings?.customHeaderColor || '#ffffff',
      customAccentColor: initialSettings?.customAccentColor || '#6366f1',
    },
  });

  const handleFormChange = () => {
    const values = form.getValues();
    onSettingsChange?.(values);
  };

  return (
    <div className="max-w-7xl space-y-6">
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Theme and Logo Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Palette className="h-5 w-5" />
              Theme & Branding
            </CardTitle>
            <CardDescription>Configure the overall look and branding of your parent portal</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <Form {...form}>
              <FormField
                control={form.control}
                name="theme"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Portal Theme</FormLabel>
                    <Select
                      disabled={readOnly}
                      onValueChange={(value) => {
                        field.onChange(value);
                        handleFormChange();
                      }}
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select a theme" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="default">Default</SelectItem>
                        <SelectItem value="light">Light</SelectItem>
                        <SelectItem value="dark">Dark</SelectItem>
                        <SelectItem value="colorful">Colorful</SelectItem>
                        <SelectItem value="minimal">Minimal</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormDescription>
                      Choose a theme for your parent portal
                    </FormDescription>
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="logoEnabled"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">Studio Logo</FormLabel>
                      <FormDescription>
                        Display your studio logo in the parent portal
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={(checked) => {
                          field.onChange(checked);
                          handleFormChange();
                        }}
                        disabled={readOnly}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </Form>
          </CardContent>
        </Card>

        {/* Color Customization */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Layout className="h-5 w-5" />
              Color Customization
            </CardTitle>
            <CardDescription>Customize colors to match your studio's brand</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <Form {...form}>
              <FormField
                control={form.control}
                name="customHeaderColor"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Header Color</FormLabel>
                    <div className="flex items-center gap-3">
                      <FormControl>
                        <Input
                          type="text"
                          placeholder="#ffffff"
                          {...field}
                          onChange={(e) => {
                            field.onChange(e.target.value);
                            handleFormChange();
                          }}
                          disabled={readOnly}
                          className="flex-1"
                        />
                      </FormControl>
                      <input
                        type="color"
                        value={field.value}
                        onChange={(e) => {
                          field.onChange(e.target.value);
                          handleFormChange();
                        }}
                        className="h-10 w-16 border rounded cursor-pointer"
                        disabled={readOnly}
                      />
                    </div>
                    <FormDescription>
                      Customize the header color of the parent portal
                    </FormDescription>
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="customAccentColor"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Accent Color</FormLabel>
                    <div className="flex items-center gap-3">
                      <FormControl>
                        <Input
                          type="text"
                          placeholder="#6366f1"
                          {...field}
                          onChange={(e) => {
                            field.onChange(e.target.value);
                            handleFormChange();
                          }}
                          disabled={readOnly}
                          className="flex-1"
                        />
                      </FormControl>
                      <input
                        type="color"
                        value={field.value}
                        onChange={(e) => {
                          field.onChange(e.target.value);
                          handleFormChange();
                        }}
                        className="h-10 w-16 border rounded cursor-pointer"
                        disabled={readOnly}
                      />
                    </div>
                    <FormDescription>
                      Customize the accent color for buttons and highlights
                    </FormDescription>
                  </FormItem>
                )}
              />
            </Form>
          </CardContent>
        </Card>
      </div>

      {/* Preview Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Image className="h-5 w-5" />
            Preview
          </CardTitle>
          <CardDescription>See how your customizations will look in the parent portal</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="rounded-lg border-2 border-dashed border-gray-300 p-8 text-center">
            <p className="text-muted-foreground">Portal preview coming soon</p>
            <p className="text-sm text-muted-foreground mt-2">Changes will be reflected in real-time when preview is available</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ParentPortalAppearanceTab;
